import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { usePermissions } from "./usePermissions";
import type { GovernancePermission } from "./permissions";

export interface ActionGateProps {
  permission: GovernancePermission;
  children: ReactNode;
  /**
   * `disable` (default) keeps the action visible and explains why it is off —
   * a user should be able to see what to ask an administrator for.
   * `hide` removes it entirely, for navigation and bulk toolbars where a dead
   * control is noise.
   */
  when?: "disable" | "hide";
  /** Rendered instead of the children when hidden or denied. */
  fallback?: ReactNode;
}

/**
 * Presents a governed action according to the advisory verdict.
 *
 * | verdict   | rendered                                                     |
 * |-----------|--------------------------------------------------------------|
 * | granted   | children, untouched                                           |
 * | unknown   | children, untouched — the backend decides when the user acts  |
 * | denied    | disabled child + the backend's reason, or nothing when hidden |
 *
 * A rendered, enabled action is never proof of authorization: the Worker and
 * the Policy AI backend both re-check, and a 403 is surfaced verbatim.
 */
export function ActionGate({ permission, children, when = "disable", fallback }: ActionGateProps) {
  const { can } = usePermissions();
  const verdict = can(permission);
  const reasonId = useId();

  if (verdict.allowed) return <>{children}</>;
  if (when === "hide") return <>{fallback ?? null}</>;

  const disabled = isValidElement(children)
    ? cloneElement(children as ReactElement<{ disabled?: boolean; "aria-describedby"?: string }>, {
        disabled: true,
        "aria-describedby": reasonId,
      })
    : children;

  return (
    <span className="ods-gate">
      {disabled}
      <span className="ods-gate__reason" id={reasonId}>
        {verdict.reason}
      </span>
    </span>
  );
}
